import { useState, type FormEvent } from 'react';
import { MAX_INTERESTS, normalizeInterest } from '@phidio/protocol';
import { engine } from '../hooks/useEngine.ts';
import { CloseIcon } from './Icons.tsx';

const SUGGESTIONS = [
  'music',
  'gaming',
  'football',
  'travel',
  'movies',
  'startups',
  'art',
  'anime',
  'coding',
  'photography',
  'fitness',
  'cooking',
];

interface Props {
  interests: string[];
  strict: boolean;
}

export function InterestPicker({ interests, strict }: Props) {
  const [draft, setDraft] = useState('');
  const full = interests.length >= MAX_INTERESTS;

  const add = (raw: string) => {
    const tag = normalizeInterest(raw);
    if (tag.length < 2 || full || interests.includes(tag)) return;
    engine.updateProfile({ interests: [...interests, tag] });
    setDraft('');
  };

  const remove = (tag: string) => {
    engine.updateProfile({ interests: interests.filter((item) => item !== tag) });
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    add(draft);
  };

  const available = SUGGESTIONS.filter((tag) => !interests.includes(tag)).slice(0, 8);

  return (
    <div className="w-full max-w-xl text-left">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <label htmlFor="interest-input" className="text-sm font-medium text-white/70">
          What are you into?
        </label>
        <span className="text-xs text-white/40">
          {interests.length}/{MAX_INTERESTS} · better matches
        </span>
      </div>

      <form onSubmit={submit} className="flex gap-2">
        <input
          id="interest-input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder={full ? 'Interest list is full' : 'Add an interest and hit enter'}
          disabled={full}
          maxLength={20}
          autoComplete="off"
          className="glass min-w-0 flex-1 rounded-2xl px-4 py-3 text-white placeholder:text-white/35 outline-none transition focus:border-phidio-500/70 focus:ring-2 focus:ring-phidio-500/30 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={full || draft.trim().length < 2}
          className="rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white transition hover:bg-white/20 disabled:opacity-30"
        >
          Add
        </button>
      </form>

      {interests.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {interests.map((tag) => (
            <li key={tag}>
              <button
                type="button"
                onClick={() => remove(tag)}
                className="group flex items-center gap-1.5 rounded-full bg-phidio-500/20 py-1.5 pl-3 pr-2 text-sm font-medium text-phidio-200 ring-1 ring-phidio-500/40 transition hover:bg-phidio-500/30"
              >
                #{tag}
                <CloseIcon width={14} height={14} className="opacity-50 group-hover:opacity-100" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {available.length > 0 && !full && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {available.map((tag) => (
            <li key={tag}>
              <button
                type="button"
                onClick={() => add(tag)}
                className="rounded-full border border-white/10 px-3 py-1.5 text-sm text-white/50 transition hover:border-phidio-500/50 hover:text-white"
              >
                +&nbsp;{tag}
              </button>
            </li>
          ))}
        </ul>
      )}

      <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm text-white/60">
        <span className="relative inline-block h-6 w-11 shrink-0">
          <input
            type="checkbox"
            checked={strict}
            onChange={(event) => engine.updateProfile({ strict: event.target.checked })}
            className="peer sr-only"
          />
          <span className="absolute inset-0 rounded-full bg-white/15 transition peer-checked:bg-phidio-500 peer-focus-visible:ring-2 peer-focus-visible:ring-phidio-400" />
          <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white transition peer-checked:translate-x-5" />
        </span>
        Only match me with people who share an interest
      </label>
    </div>
  );
}
